import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TableContainer,
  Paper,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { ErrorService } from "@services/errorService";
import { ApiError } from "@/types/api";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader } from "./card";
import { Edit, Eye, Search, Trash2Icon } from "lucide-react";
import { ScrollArea, ScrollBar } from "./scroll-area";
import { ProductsGridSkeleton } from "./skeleton";

export type Column<T> = {
  key: keyof T;
  header: string;
  image?: keyof T;
  placeholderImage?: string;
  style_image?: {
    border?: string;
    borderRadius?: number;
    background?: string;
  };
  render?: (value: T[keyof T] | any, item: T) => React.ReactNode;
  width?: number | string;
};

type Action<T> = {
  id?: string;
  label: string;
  icon: React.ReactNode;
  onClick: (item: T) => void;
  disabled?: boolean | ((item: T) => boolean);
};

type DataTableProps<T> = {
  data: T[];
  columns: Column<T>[];
  basePath: string;
  onDelete?: (id: string) => void;
  children?: () => any;
  loading?: boolean;
  title?: string;
  subtitle?: string;
  error?: ApiError | null;
  loadingComponent?: React.ReactNode;
  actions?: Action<T>[];
  hideEdit?: boolean;
  newFormat?: boolean;
  formatGrid?: boolean;
  hide_header?: boolean;
  hide_search?: boolean;
  hide_actions?: boolean;
  hide_add?: boolean;
  selectedRow?: any;
  onClickRow?: any;
  hideCrud?: boolean;
  hide_show?:boolean;
  rowHeight?: number;
  renderItem?: (item: T, index: number) => React.ReactNode;
  /**
   * Estilo extra aplicado por linha (no newFormat). Útil pra pintar a linha
   * com fundo dependente do estado do item — ex.: fundo azul translúcido
   * para FilledForms ainda não baixados.
   */
  getRowStyle?: (item: T) => React.CSSProperties | undefined;
};

function DataTable<T extends { _id?: string; id?: string }>({
  data,
  title,
  columns,
  basePath,
  onDelete,
  loading,
  error,
  loadingComponent,
  actions,
  hideEdit,
  newFormat,
  formatGrid,
  children,
  subtitle,
  hide_header,
  hide_search,
  hide_actions,
  hide_add,
  selectedRow,
  onClickRow,
  hide_show,
  hideCrud,
  rowHeight = 75,
  renderItem,
  getRowStyle,
}: DataTableProps<T>): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  // Paginação 25/pág no newFormat (restaurada — estava em prod via patch,
  // não chegou ao source). Reset ao mudar search ou tamanho dos dados.
  const PAGE_SIZE = 25;
  const [currentPage, setCurrentPage] = useState(1);
  React.useEffect(() => {
    setCurrentPage(1);
  }, [search, data?.length]);

  if (loading) {
    return (
      <>
        {loadingComponent || (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}
      </>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">
          {typeof error === "string"
            ? error
            : ErrorService.getFormattedMessage(error)}
        </Typography>
      </Box>
    );
  }
  console.log("data", data);

  if (!Array.isArray(data)) {
    return (
      <Box p={3}>
        <Typography color="error">Nenhum dado disponível</Typography>
      </Box>
    );
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // RESTAURACAO R5: search agora funciona pra qualquer modelo (antes so checava item.name).
  // Serializa item -> string e procura case-insensitive. Cobre nome do cliente em
  // FilledForm.data[].data, status, tipo, etc, sem precisar conhecer schema.
  const matchesSearch = (item: any): boolean => {
    if (!search) return true;
    try {
      return JSON.stringify(item).toLowerCase().includes(search.toLowerCase());
    } catch {
      return true;
    }
  };
  console.log("data", data);
  return (
    <Box>
      {/* <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/${basePath}/new`)}
        >
          Novo
        </Button>
      </Box> */}
      {hide_header != true && (
        <div className=" justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">{title}</h1>
          {subtitle && (
            <p style={{ color: "grey" }} className="mt-1">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {children && children()}
      {hide_search != true && (
        <div className="mb-4 flex w-full" style={{ position: "relative" }}>
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Pesquisar"
            className="p-2 border rounded"
            style={{
              width: "-webkit-fill-available",
              marginRight: 20,
              borderRadius: 20,
              paddingLeft: 40,
            }}
          />
          <Search
            style={{
              position: "absolute",
              top: "20%",
              left: 10,
              color: "grey",
            }}
          />
          {hide_add != true && (
            <Link
              to={`/${basePath}/new`}
              style={{ width: 160, borderRadius: 20 }}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              <p style={{ whiteSpace: "nowrap" }}>
                {"Adicionar"} <AddIcon />
              </p>
            </Link>
          )}
        </div>
      )}

      {formatGrid && (
        <Card
          style={{ maxHeight: window.innerHeight - 280, overflow: "scroll" }}
          className="custom-scrollbar mt-2 w-full border border-[#D8E0F0] rounded-[14px] bg-[#FCFCFC] hover:border-[#c8d0e0] transition-colors custom-scrollbar"
        >
          <CardContent className="flex flex-col p-0 relative">
            <ScrollArea className="w-full h-full">
              {loading == false &&
                data.filter(
                  (item: any) =>
                    matchesSearch(item),
                ).length == 0 && (
                  <div>
                    <p style={{ textAlign: "center", marginTop: 50 }}>
                      Nenhum registro encontrado
                    </p>
                  </div>
                )}
              {loading ? (
                <ProductsGridSkeleton />
              ) : (
                <div
                  className="grid auto-rows-[320px] gap-[20px] p-[25px]"
                  style={{
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(230px, 1fr))",
                    gridAutoFlow: "dense",
                  }}
                >
                  {data
                    .filter(
                      (item: any) =>
                        matchesSearch(item),
                    )
                    .map((card: any, index) => (
                      <Card
                        key={card._id}
                        style={{ maxWidth: 270 }}
                        className={`flex flex-col h-full rounded-xl overflow-hidden border border-solid cursor-pointer ${"border-[#d8e0f0]"}`}
                      >
                        <CardHeader
                          className={`h-[46px] flex w-full items-center justify-center px-0 py-3.5 bg-clarofrente-01 rounded-[12px_12px_0px_0px] border-b-[0.8px] [border-bottom-style:solid] ${"border-[#d8e0f0]"} p-0`}
                        >
                          <div className="flex w-full items-center justify-between px-2.5">
                            <div
                              style={{ color: "grey" }}
                              className="font-inter-16-medium font-[number:var(--inter-16-medium-font-weight)] text-clarotextos-02 text-[length:var(--inter-16-medium-font-size)] whitespace-nowrap tracking-[var(--inter-16-medium-letter-spacing)] leading-[var(--inter-16-medium-line-height)] [font-style:var(--inter-16-medium-font-style)]"
                            >
                              {String(index + 1).padStart(2, "0")}
                            </div>
                            <div className="inline-flex h-[18px] items-center justify-center gap-2">
                              {/* {card._id !== "geral" && items.find(p => p._id === card.id) && findFirstFormActivity(items.find(p => p._id === card.id)!) && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <svg 
                                                  className={`cursor-pointer ${linkCopied === card._id ? 'opacity-50' : ''} w-[18px] h-[18px]`}
                                                  viewBox="0 0 24 24" 
                                                  fill="none" 
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  onClick={(e) => {
                                                    e.stopPropagation(); // Evita acionar o onClick do Card
                                                    const product = items.find(p => p._id === card.id);
                                                    if (product) handleGenerateLink(product);
                                                  }}
                                                >
                                                  <path 
                                                    d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2358 14.7367 12.9315 14.9411 13.6467 14.9913C14.3618 15.0415 15.0796 14.9364 15.7517 14.6828C16.4238 14.4291 17.0338 14.0329 17.54 13.52L20.54 10.52C21.4508 9.59695 21.9548 8.33397 21.9436 7.02299C21.9324 5.71201 21.4068 4.45794 20.479 3.53004C19.5511 2.60214 18.297 2.07659 16.986 2.06537C15.675 2.05415 14.4121 2.55821 13.489 3.46904L11.639 5.31004" 
                                                    stroke="currentColor" 
                                                    strokeWidth="2" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                  />
                                                  <path 
                                                    d="M14 11C13.5705 10.4259 13.0226 9.95085 12.3934 9.60705C11.7642 9.26325 11.0685 9.05888 10.3534 9.00868C9.63821 8.95849 8.92041 9.06363 8.24826 9.31723C7.57612 9.57082 6.96609 9.96703 6.46 10.48L3.46 13.48C2.54917 14.403 2.04511 15.666 2.05633 16.977C2.06755 18.288 2.5931 19.5421 3.521 20.47C4.4489 21.3979 5.70297 21.9234 7.01395 21.9346C8.32493 21.9458 9.58791 21.4418 10.511 20.531L12.361 18.69" 
                                                    stroke="currentColor" 
                                                    strokeWidth="2" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>Copiar link do formulário</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )} */}
                              {hideCrud == undefined && (
                                <img
                                  className="cursor-pointer"
                                  alt="Edit"
                                  src="/01.svg"
                                  style={{ width: 20, height: 20 }}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Evita acionar o onClick do Card
                                    navigate(
                                      `/${basePath}/edit/${card._id || card.id || ""}`,
                                    );
                                  }}
                                />
                              )}
                              {/* <img className=""
                          onClick={(e) => {
                              e.stopPropagation(); // Evita acionar o onClick do Card
                              navigate(`/${basePath}/edit/${card._id || card.id || ""}`)
                            }}
                          alt="Element" src="/02-1.svg" /> */}
                              {hideCrud == undefined && (
                                <img
                                  className="cursor-pointer"
                                  alt="Delete"
                                  src="/03.svg"
                                  style={{ width: 20, height: 20 }}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Evita acionar o onClick do Card
                                    console.log(card);
                                    const id = card._id || card.id || "";
                                    if (id && onDelete) onDelete(id);
                                    // handleDeleteClick(card.id, card.title);
                                  }}
                                />
                              )}
                              {actions?.map((action, index) => {
                                const isDisabled =
                                  typeof action.disabled === "function"
                                    ? action.disabled(card)
                                    : action.disabled;
                                return (
                                  <div
                                    key={index}
                                    style={{
                                      marginRight: 0,
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      background: "#fff",
                                      borderRadius: 10,
                                      width: 30,
                                      height: 30,
                                      color: "grey",
                                      opacity: isDisabled ? 0.5 : 1,
                                      pointerEvents: isDisabled
                                        ? "none"
                                        : "auto",
                                    }}
                                  >
                                    <IconButton
                                      style={{
                                        borderRadius: 10,
                                        width: 30,
                                        height: 30,
                                        color: "grey",
                                      }}
                                      onClick={() => action.onClick(card)}
                                      title={action.label}
                                      disabled={isDisabled}
                                    >
                                      {action.icon}
                                    </IconButton>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="p-0 h-[318px] flex flex-col">
                          <div className="flex items-center gap-2.5 p-2.5">
                            <div
                              className="flex items-center justify-center gap-2.5 flex-1"
                              style={{
                                borderRadius: 10,
                                backgroundColor: "#f4f4f4",
                                height: 100,
                              }}
                            >
                              <div
                                style={{ color: "grey", textAlign: "center" }}
                                className="font-inter-14-regular font-[number:var(--inter-14-regular-font-weight)] text-clarotextos-01 text-[length:var(--inter-14-regular-font-size)] tracking-[var(--inter-14-regular-letter-spacing)] leading-[var(--inter-14-regular-line-height)] [font-style:var(--inter-14-regular-font-style)]"
                              >
                                {card.title}
                              </div>
                            </div>
                          </div>

                          <div
                            className="flex-1 w-full"
                            type="scroll"
                            style={{ padding: 10 }}
                          >
                            <div
                              className="flex flex-col pt-0 pb-[5px] px-[15px] rounded-[0px_0px_8px_8px]"
                              style={{
                                borderRadius: 10,
                                backgroundColor: "#f4f4f4",
                              }}
                            >
                              <div
                                key={`${card._id}-${card.title}`}
                                className="flex items-center gap-2.5 px-0 py-2.5 w-full hover:bg-[#f4f4f4] rounded-[6px] transition-colors duration-200 cursor-pointer"
                              >
                                {/* <img
                                className={`${card.icon_type.includes("cone-30")
                                    ? "w-[17.2px] h-[14.2px] ml-[-1.60px]"
                                    : card.icon_type.includes("cone.svg")
                                      ? "w-[16.3px] h-[14.3px] ml-[-1.15px]"
                                      : card.icon_type.includes("cone-4")
                                        ? "w-3.5 h-[14.3px]"
                                        : ""
                                  }`}
                                alt="Icon"
                                src={card.icon_type}
                              /> */}
                                {card.icon_type}
                                <div className="flex items-center gap-2.5 flex-1">
                                  <div
                                    style={{ color: "grey" }}
                                    className="font-inter-14-regular text-clarotextos-01"
                                  >
                                    {card.type}
                                  </div>
                                </div>
                              </div>
                              <hr />

                              {card.stats.map((stat: any) => (
                                <div
                                  key={`${card._id}-${stat.name}`}
                                  className="flex items-center gap-2.5 px-0 py-2.5 w-full hover:bg-[#f4f4f4] rounded-[6px] transition-colors duration-200 cursor-pointer"
                                >
                                  {stat.icon}
                                  <div className="flex items-center gap-2.5 flex-1">
                                    <div
                                      style={{ color: "grey" }}
                                      className="font-inter-14-regular text-clarotextos-01"
                                    >
                                      {stat.name}
                                    </div>
                                  </div>
                                  <div
                                    style={{ color: "grey" }}
                                    className="font-inter-14-regular text-clarotextos-01"
                                  >
                                    {stat.count}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              )}
              <ScrollBar orientation="vertical" />
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {!formatGrid && (
        <Card
          style={{
            maxHeight: children
              ? window.innerHeight - 300
              : window.innerHeight - 200,
            overflow: "scroll",
          }}
          className="custom-scrollbar w-full border border-[#D8E0F0] rounded-[14px] bg-[#FCFCFC] hover:border-[#c8d0e0] transition-colors"
        >
          <CardContent className="p-[20px] ">
            <ScrollArea className="w-full h-full">
              {/* {loading == false &&
                data.filter(
                  (item: any) =>
                    matchesSearch(item),
                ).length == 0 && (
                  <div>
                    <p style={{ textAlign: "center", marginTop: 50 }}>
                      Nenhum registro encontrado
                    </p>
                  </div>
                )} */}

              {renderItem ? (
                <div className="flex flex-col gap-4 p-4">
                  {data
                    .filter(
                      (item: any) =>
                        matchesSearch(item),
                    )
                    .map((item, index) => (
                      <React.Fragment key={item._id || item.id || index}>
                        {renderItem(item, index)}
                      </React.Fragment>
                    ))}
                </div>
              ) : (
                <>
                  {newFormat && (() => {
                    const filtered = data.filter(
                      (item: any) => matchesSearch(item),
                    );
                    const totalPages = Math.max(
                      1,
                      Math.ceil(filtered.length / PAGE_SIZE),
                    );
                    const clampedPage = Math.min(currentPage, totalPages);
                    const startIdx = (clampedPage - 1) * PAGE_SIZE;
                    const pageItems = filtered.slice(
                      startIdx,
                      startIdx + PAGE_SIZE,
                    );
                    return (
                    <>
                    <table
                      className="w-full"
                      style={{
                        borderCollapse: "separate",
                        borderSpacing: "0 0",
                      }}
                    >
                      <tbody>
                        {filtered.length === 0 ? (
                          <tr>
                            <td
                              className="py-2 px-4 border"
                              colSpan={columns.length + 1}
                              align="center"
                            >
                              Nenhum registro encontrado
                            </td>
                          </tr>
                        ) : (
                          pageItems
                            .map((item) => {
                              const rowExtraStyle = getRowStyle?.(item) || {};
                              return (
                              <>
                                <tr
                                  style={{
                                    cursor: onClickRow ? "pointer" : "default",
                                  }}
                                  onClick={() => {
                                    if (onClickRow) {
                                      console.log(item, selectedRow);
                                      onClickRow(item);
                                    }
                                  }}
                                  className="mb-2"
                                >
                                  {columns.map((column, id) => (
                                    <>
                                      {column.image && (
                                        <td
                                          style={{
                                            width: 70,
                                            margin: 0,
                                            padding: 0,
                                          }}
                                        >
                                          <div
                                            style={{
                                              padding: 10,
                                              height: rowHeight,
                                              border:
                                                selectedRow != undefined &&
                                                selectedRow == item.id
                                                  ? "1px solid #1776cf"
                                                  : "1px solid lightgrey",
                                              borderRadius: 10,
                                              borderTopRightRadius: 0,
                                              borderBottomRightRadius: 0,
                                              borderRight: 0,
                                              borderLeft:
                                                id == 0
                                                  ? selectedRow != undefined &&
                                                    selectedRow == item.id
                                                    ? "1px solid #1776cf"
                                                    : "1px solid lightgrey"
                                                  : 0,
                                              borderTopLeftRadius:
                                                id == 0 ? 10 : 0,
                                              borderBottomLeftRadius:
                                                id == 0 ? 10 : 0,
                                              background: rowExtraStyle.background,
                                            }}
                                          >
                                            <div
                                              style={{
                                                border: column.style_image
                                                  ?.border
                                                  ? column.style_image?.border
                                                  : "1px solid rgb(37 99 235 / var(--tw-bg-opacity, 1))",
                                                borderRight: column.style_image
                                                  ?.border
                                                  ? column.style_image?.border
                                                  : 0,
                                                borderRadius: column.style_image
                                                  ?.borderRadius
                                                  ? column.style_image
                                                      ?.borderRadius
                                                  : 100,
                                                minWidth: 51,
                                                minHeight: 51,
                                                width: 51,
                                                height: 51,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                background: column.style_image
                                                  ?.background
                                                  ? column.style_image
                                                      ?.background
                                                  : "white",
                                              }}
                                            >
                                              {
                                                <img
                                                  style={{
                                                    minWidth: 45,
                                                    minHeight: 45,
                                                    width: 45,
                                                    height: 45,
                                                    objectFit: "cover",
                                                    borderRadius: column
                                                      .style_image?.borderRadius
                                                      ? column.style_image
                                                          ?.borderRadius
                                                      : 100,
                                                  }}
                                                  src={
                                                    item[column.image]
                                                      ? item[column.image]
                                                      : column.placeholderImage
                                                        ? column.placeholderImage
                                                        : "https://microbiology.ucr.edu/sites/default/files/styles/form_preview/public/blank-profile-pic.png?itok=4teBBoet"
                                                  }
                                                />
                                              }
                                            </div>
                                          </div>
                                        </td>
                                      )}

                                      <td
                                        style={{
                                          minWidth: column.width || 150,
                                          margin: 0,
                                          padding: 0,
                                        }}
                                      >
                                        <div
                                          style={{
                                            padding: 10,
                                            height: rowHeight,
                                            background: rowExtraStyle.background,
                                            border:
                                              selectedRow != undefined &&
                                              selectedRow == item.id
                                                ? "1px solid #1776cf"
                                                : "1px solid lightgrey",
                                            borderRadius: 10,
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0,
                                            borderRight: 0,
                                            borderLeft:
                                              id == 0 && !column.image
                                                ? selectedRow != undefined &&
                                                  selectedRow == item.id
                                                  ? "1px solid #1776cf"
                                                  : "1px solid lightgrey"
                                                : 0,
                                            borderTopLeftRadius:
                                              id == 0 && !column.image ? 10 : 0,
                                            borderBottomLeftRadius:
                                              id == 0 && !column.image ? 10 : 0,
                                          }}
                                        >
                                          <div>
                                            <p
                                              style={{
                                                color: "grey",
                                                fontSize: 12,
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                width: 100,
                                                display: "block",
                                                overflow: "hidden",
                                                margin: 0,
                                                padding: 0,
                                                lineHeight: "14px",
                                                marginBottom: "8px",
                                                marginLeft: "2px",
                                              }}
                                            >
                                              {column.header}
                                            </p>
                                            <div
                                              style={{
                                                color: "black",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: 240,
                                                display: "block",
                                                overflow: "hidden",
                                                lineHeight: "1.2",
                                              }}
                                            >
                                              <b
                                                style={{ fontWeight: "normal" }}
                                              >
                                                {column.render
                                                  ? column.render(
                                                      item[column.key],
                                                      item,
                                                    )
                                                  : String(
                                                      item[column.key] || "-",
                                                    )}
                                              </b>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    </>
                                  ))}
                                  {hide_actions ? (
                                    <td
                                      style={{
                                        position: "sticky",
                                        right: 0,
                                        zIndex: 2,
                                        background: "#FCFCFC",
                                      }}
                                    >
                                      <div
                                        className="flex items-center"
                                        style={{
                                          justifyContent: "flex-end",
                                          height: rowHeight,
                                          padding: 10,
                                          background: rowExtraStyle.background,
                                          border:
                                            selectedRow != undefined &&
                                            selectedRow == item.id
                                              ? "1px solid #1776cf"
                                              : "1px solid lightgrey",
                                          borderRadius: 10,
                                          borderLeft: 0,
                                          borderTopLeftRadius: 0,
                                          borderBottomLeftRadius: 0,
                                        }}
                                      ></div>
                                    </td>
                                  ) : (
                                    <td
                                      style={{
                                        margin: 0,
                                        padding: 0,
                                        position: "sticky",
                                        right: 0,
                                        zIndex: 2,
                                        background: "#FCFCFC",
                                      }}
                                    >
                                      <div
                                        className="flex items-center"
                                        style={{
                                          justifyContent: "flex-end",
                                          background: rowExtraStyle.background,
                                          height: rowHeight,
                                          padding: 10,
                                          border:
                                            selectedRow != undefined &&
                                            selectedRow == item.id
                                              ? "1px solid #1776cf"
                                              : "1px solid lightgrey",
                                          borderRadius: 10,
                                          borderLeft: 0,
                                          borderTopLeftRadius: 0,
                                          borderBottomLeftRadius: 0,
                                        }}
                                      >
                                        {!hideEdit && (
                                          <div
                                            style={{
                                              marginRight: 5,
                                              display: "flex",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              background: "#f2f2f2",
                                              borderRadius: 10,
                                              width: 35,
                                              height: 35,
                                              color: "grey",
                                            }}
                                          >
                                            <IconButton
                                              style={{
                                                borderRadius: 10,
                                                width: 35,
                                                height: 35,
                                                color: "grey",
                                              }}
                                              onClick={() =>
                                                navigate(
                                                  `/${basePath}/edit/${item._id || item.id || ""}`,
                                                )
                                              }
                                            >
                                              <Edit />
                                            </IconButton>
                                          </div>
                                        )}
                                        {!hideEdit && !hide_show && (
                                          <div
                                            style={{
                                              marginRight: 5,
                                              display: "flex",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              background: "#f2f2f2",
                                              borderRadius: 10,
                                              width: 35,
                                              height: 35,
                                              color: "grey",
                                            }}
                                          >
                                            <IconButton
                                              style={{
                                                borderRadius: 10,
                                                width: 35,
                                                height: 35,
                                                color: "grey",
                                              }}
                                              onClick={() =>
                                                navigate(
                                                  `/${basePath}/view/${item._id || item.id || ""}`,
                                                )
                                              }
                                            >
                                              <Eye />
                                            </IconButton>
                                          </div>
                                        )}
                                        {onDelete && (
                                          <div
                                            style={{
                                              marginRight: 5,
                                              display: "flex",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              background: "#f2f2f2",
                                              borderRadius: 10,
                                              width: 35,
                                              height: 35,
                                              color: "grey",
                                            }}
                                          >
                                            <IconButton
                                              style={{
                                                borderRadius: 10,
                                                width: 35,
                                                height: 35,
                                                color: "grey",
                                              }}
                                              onClick={() => {
                                                const id = item._id || item.id;
                                                if (id) onDelete(id);
                                              }}
                                            >
                                              <Trash2Icon />
                                            </IconButton>
                                          </div>
                                        )}
                                        {actions?.map((action, index) => {
                                          const isDisabled =
                                            typeof action.disabled ===
                                            "function"
                                              ? action.disabled(item)
                                              : action.disabled;

                                          return (
                                            (action.id != "send_doc" ||
                                              item?.data?.filter(
                                                (item3: any) =>
                                                  item3.field.type ==
                                                  "BINDING_QUESTION",
                                              )?.length > 0) && (
                                              <div
                                                key={index}
                                                style={{
                                                  marginRight: 5,
                                                  display: "flex",
                                                  justifyContent: "center",
                                                  alignItems: "center",
                                                  background: "#f2f2f2",
                                                  borderRadius: 10,
                                                  width: 35,
                                                  height: 35,
                                                  color: "grey",
                                                  opacity: isDisabled ? 0.5 : 1,
                                                  pointerEvents: isDisabled
                                                    ? "none"
                                                    : "auto",
                                                }}
                                              >
                                                <IconButton
                                                  style={{
                                                    borderRadius: 10,
                                                    width: 35,
                                                    height: 35,
                                                    color: "grey",
                                                  }}
                                                  onClick={() =>
                                                    action.onClick(item)
                                                  }
                                                  title={action.label}
                                                  disabled={isDisabled}
                                                >
                                                  {action.icon}
                                                </IconButton>
                                              </div>
                                            )
                                          );
                                        })}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                                <tr style={{ height: 10 }}></tr>
                              </>
                              );
                            })
                        )}
                      </tbody>
                    </table>
                    {filtered.length > PAGE_SIZE && (
                      <div
                        className="mt-4 px-2 py-2 text-sm text-gray-600 flex flex-wrap items-center gap-x-6 gap-y-2"
                        style={{
                          position: "sticky",
                          left: 0,
                          width: "max-content",
                          maxWidth: "100%",
                        }}
                      >
                        <span className="whitespace-nowrap">
                          Mostrando {startIdx + 1}–
                          {Math.min(startIdx + PAGE_SIZE, filtered.length)} de{" "}
                          {filtered.length}
                        </span>
                        <div className="flex items-center gap-1 flex-wrap">
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center rounded border bg-white disabled:opacity-40 hover:bg-gray-50 leading-none"
                            onClick={() => setCurrentPage(1)}
                            disabled={clampedPage <= 1}
                            title="Primeira página"
                            aria-label="Primeira página"
                          >
                            «
                          </button>
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center rounded border bg-white disabled:opacity-40 hover:bg-gray-50 leading-none"
                            onClick={() =>
                              setCurrentPage((p) => Math.max(1, p - 1))
                            }
                            disabled={clampedPage <= 1}
                            title="Página anterior"
                            aria-label="Página anterior"
                          >
                            ‹
                          </button>
                          <span className="px-2 flex items-center gap-1 whitespace-nowrap">
                            <input
                              type="number"
                              min={1}
                              max={totalPages}
                              value={clampedPage}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                if (!Number.isNaN(v)) {
                                  setCurrentPage(
                                    Math.min(totalPages, Math.max(1, v)),
                                  );
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  (e.target as HTMLInputElement).blur();
                              }}
                              className="w-12 h-8 px-1 rounded border text-center font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300"
                              aria-label={`Ir para página entre 1 e ${totalPages}`}
                              title="Digite o número da página e tecle Enter"
                            />
                            <span>
                              / <strong>{totalPages}</strong>
                            </span>
                          </span>
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center rounded border bg-white disabled:opacity-40 hover:bg-gray-50 leading-none"
                            onClick={() =>
                              setCurrentPage((p) => Math.min(totalPages, p + 1))
                            }
                            disabled={clampedPage >= totalPages}
                            title="Próxima página"
                            aria-label="Próxima página"
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center rounded border bg-white disabled:opacity-40 hover:bg-gray-50 leading-none"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={clampedPage >= totalPages}
                            title="Última página"
                            aria-label="Última página"
                          >
                            »
                          </button>
                        </div>
                      </div>
                    )}
                    </>
                    );
                  })()}

                  {!newFormat && (
                    <TableContainer component={Paper}>
                      <table className="min-w-full bg-white border">
                        <thead>
                          <tr>
                            {columns.map((column) => (
                              <th
                                className="py-2 px-4 border"
                                key={String(column.key)}
                              >
                                {column.header}
                              </th>
                            ))}
                            <th
                              className="py-2 px-4 border"
                              sx={{ minWidth: "150px" }}
                              align="right"
                            >
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.filter(
                            (item: any) =>
                              matchesSearch(item),
                          ).length === 0 ? (
                            <tr>
                              <td
                                className="py-2 px-4 border"
                                colSpan={columns.length + 1}
                                align="center"
                              >
                                Nenhum registro encontrado
                              </td>
                            </tr>
                          ) : (
                            data
                              .filter(
                                (item: any) =>
                                  matchesSearch(item),
                              )
                              .map((item) => (
                                <tr key={item._id} hover>
                                  {columns.map((column) => (
                                    <td
                                      className="py-2 px-4 border"
                                      key={String(column.key)}
                                    >
                                      {column.render
                                        ? column.render(item[column.key], item)
                                        : String(item[column.key] || "")}
                                    </td>
                                  ))}
                                  <td
                                    className="py-2 px-4 border"
                                    align="right"
                                  >
                                    {!hideEdit && (
                                      <IconButton
                                        color="primary"
                                        onClick={() =>
                                          navigate(
                                            `/${basePath}/edit/${item._id || item.id || ""}`,
                                          )
                                        }
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    )}
                                    {onDelete && (
                                      <IconButton
                                        color="error"
                                        onClick={() => {
                                          const id = item._id || item.id;
                                          if (id) onDelete(id);
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    )}
                                    {actions?.map((action, index) => (
                                      <IconButton
                                        key={index}
                                        onClick={() => action.onClick(item)}
                                        title={action.label}
                                      >
                                        {action.icon}
                                      </IconButton>
                                    ))}
                                  </td>
                                </tr>
                              ))
                          )}
                        </tbody>
                      </table>
                    </TableContainer>
                  )}
                </>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default DataTable;
